import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useWorkOrderRepository } from '@ayocore/erp/crm/client/rpc';
import type { CreateWorkOrder, WorkOrder } from '@ayocore/erp/crm/schemas/work-order.schema';
import {
  createWorkOrderSupplierColumn,
  workOrderDescriptionColumn,
  workOrderQtyColumn,
  workOrderPriceColumn,
  workOrderAmountColumn,
  workOrderNotesColumn,
  type WorkOrderColumnKey,
  type WorkOrderPatch,
} from './EditableGrid/Columns';
import { EditableGrid, CheckboxCell } from '@ayocore/platform/client/components/EditableGrid';
import type { EditableGridColumn } from '@ayocore/platform/client/components/EditableGrid';

type DealRowWorkOrdersBlockProps = {
  dealRowId: number;
  onWorkOrdersChanged?: (dealRowId: number) => void;
};

export const DealRowWorkOrdersBlock = React.memo(function DealRowWorkOrdersBlock({
  dealRowId,
  onWorkOrdersChanged,
}: DealRowWorkOrdersBlockProps) {
  const workOrderRepo = useWorkOrderRepository();
  const workOrderRepoRef = useRef(workOrderRepo);
  const [gridKey] = useState(0);

  useEffect(() => {
    workOrderRepoRef.current = workOrderRepo;
  }, [workOrderRepo]);

  const columns = useMemo<EditableGridColumn<WorkOrder, WorkOrderPatch, WorkOrderColumnKey>[]>(
    () => [
      createWorkOrderSupplierColumn<WorkOrder>(),
      workOrderDescriptionColumn,
      workOrderQtyColumn,
      workOrderPriceColumn,
      workOrderAmountColumn,
      workOrderNotesColumn,
      CheckboxCell<WorkOrder, WorkOrderPatch, WorkOrderColumnKey>({
        key: 'approved',
        title: '✓',
        width: 40,
        getValue: row => !!row.approved_at,
        buildTogglePatch: (_row, checked) => ({
          apiPatch: { approved_at: checked ? new Date().toISOString() : null },
          localPatch: { approved_at: checked ? new Date().toISOString() : null },
        }),
      }),
    ],
    []
  );

  const dataSource = useMemo(
    () => ({
      loadRows: async () => {
        const list = await workOrderRepoRef.current.filterByDealRow(dealRowId).fetch();
        onWorkOrdersChanged?.(dealRowId);
        return list ?? [];
      },
      createRow: async (payload: CreateWorkOrder) => {
        const created = (await workOrderRepoRef.current.create(payload)) as WorkOrder;
        onWorkOrdersChanged?.(dealRowId);
        return created;
      },
      updateRow: async (rowId: number, patch: WorkOrderPatch) => {
        await workOrderRepoRef.current.update(rowId, patch);
        onWorkOrdersChanged?.(dealRowId);
      },
      deleteRow: async (rowId: number) => {
        await workOrderRepoRef.current.deleteById(rowId);
        onWorkOrdersChanged?.(dealRowId);
      },
      defaults: {
        deal: null,
        deal_id: null,
        deal_row: null,
        deal_row_id: dealRowId,
        supplier: null,
        supplier_id: null,
        supplier_name: null,
        work_description: null,
        qty: 1,
        price: 0,
        amount: 0,
        due_date: null,
        notes: null,
        approved_at: null,
        approved_by_id: null,
      },
    }),
    [dealRowId, onWorkOrdersChanged]
  );

  return (
    <Box py='xs' px='md'>
      <Group justify='space-between'>
        <Text size='xs' fw={600}>
          Услуги подрядчиков
        </Text>
      </Group>

      <EditableGrid
        key={gridKey}
        columns={columns}
        getRowId={row => row.id}
        dataSource={dataSource}
        loadingText='Загрузка услуг...'
        emptyText='Нет услуг. Добавьте услугу подрядчика.'
        tableMinWidth={0}
        actionColumnWidth={56}
        appendEmptyRow
        renderRowActions={({ rowId, state, actions }) => (
          <Tooltip label='Удалить'>
            <ActionIcon
              size='xs'
              variant='subtle'
              color='red'
              disabled={state.rowSaving || state.creatingRow}
              onClick={() => {
                if (!confirm('Удалить заказ?')) return;
                void actions.deleteRow(rowId);
              }}
            >
              <IconTrash size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      />

    </Box>
  );
});

DealRowWorkOrdersBlock.displayName = 'DealRowWorkOrdersBlock';
